import type { CrmData, CrmUser } from '../types/crm';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export type OwnerIdKind = 'uuid' | 'username';

function collectOwnerSamples(data: CrmData): string[] {
  const samples: string[] = [];
  const add = (v?: string) => {
    if (v?.trim()) samples.push(v.trim());
  };
  // clients.owner_id stores salesperson codes — exclude from person-owner detection.
  for (const c of data.contacts) add(c.owner_id);
  for (const p of data.projects) add(p.owner_id);
  for (const t of data.tasks) add(t.owner_id);
  return samples;
}

/**
 * Desktop crm_module.py stores contacts/projects/tasks owner_id as username.
 * clients.owner_id is salesperson code and is ignored here.
 */
export function detectOwnerIdKind(users: CrmUser[], data: CrmData): OwnerIdKind {
  const samples = collectOwnerSamples(data);

  if (samples.length === 0) {
    return 'username';
  }

  let idMatches = 0;
  let nameMatches = 0;
  for (const s of samples) {
    if (users.some((u) => u.id === s)) idMatches += 1;
    if (users.some((u) => u.username === s)) nameMatches += 1;
  }

  if (nameMatches > idMatches) return 'username';
  if (idMatches > nameMatches) return 'uuid';
  if (samples.some((s) => UUID_RE.test(s))) return 'uuid';
  return 'username';
}

export function alternateOwnerIdKind(kind: OwnerIdKind): OwnerIdKind {
  return kind === 'uuid' ? 'username' : 'uuid';
}

/** Include signed-in user when allowlist sync is empty or RLS-limited. */
export function mergeUsersWithProfile(users: CrmUser[], profile: CrmUser | null): CrmUser[] {
  if (!profile?.id) return users;
  if (users.some((u) => u.id === profile.id)) return users;
  return [...users, profile];
}

export function resolveUserByOwnerRef(users: CrmUser[], ref?: string | null): CrmUser | undefined {
  if (!ref) return undefined;
  return users.find((u) => u.id === ref || u.username === ref);
}

export function ownerIdForUser(user: CrmUser, kind: OwnerIdKind): string {
  return kind === 'uuid' ? user.id : user.username;
}

export function ownerLabelForRef(users: CrmUser[], ref?: string | null): string {
  const user = resolveUserByOwnerRef(users, ref);
  if (user) return user.display_name?.trim() || user.username;
  return ref ?? '—';
}

export function ownerMatchesFilter(
  rowOwner: string | undefined,
  filter: string,
  users: CrmUser[],
): boolean {
  if (!rowOwner) return false;
  if (rowOwner === filter) return true;
  const user = users.find((u) => u.username === filter);
  if (!user) return false;
  return rowOwner === user.id || rowOwner === user.username;
}

export function currentUserOwnerRef(profile: CrmUser | null, kind: OwnerIdKind): string {
  if (!profile) return '';
  return ownerIdForUser(profile, kind);
}

/** Map select value to DB owner_id (username or uuid per FK). */
export function normalizeOwnerRef(
  ref: string | undefined,
  users: CrmUser[],
  kind: OwnerIdKind,
): string | null {
  if (!ref?.trim()) return null;
  const user = resolveUserByOwnerRef(users, ref.trim());
  if (!user) return null;
  return ownerIdForUser(user, kind);
}

export function isClientsOwnerFkError(message: string): boolean {
  return /clients_owner_id_fkey/i.test(message);
}

/** Detect whether project_updates.author_id stores users.id or users.username. */
export function detectAuthorIdKind(
  users: CrmUser[],
  updates: { author_id?: string | null }[],
): OwnerIdKind {
  const samples = updates
    .map((u) => u.author_id)
    .filter((id): id is string => Boolean(id?.trim()) && id !== 'System');

  if (samples.length === 0) {
    return 'username';
  }

  let idMatches = 0;
  let nameMatches = 0;
  for (const s of samples) {
    if (users.some((u) => u.id === s)) idMatches += 1;
    if (users.some((u) => u.username === s)) nameMatches += 1;
  }

  if (nameMatches > idMatches) return 'username';
  if (idMatches > nameMatches) return 'uuid';
  if (samples.some((s) => UUID_RE.test(s))) return 'uuid';
  return 'username';
}

/** project_updates.author_id → crm_allowed_emails(username) (migration 014+). */
export function projectUpdateAuthorId(profile: CrmUser | null, users: CrmUser[]): string {
  if (!profile) {
    throw new Error('You must be signed in to post updates.');
  }
  const fromProfile = profile.username?.trim();
  if (fromProfile) return fromProfile;
  const byId = users.find((u) => u.id === profile.id);
  if (byId?.username?.trim()) return byId.username.trim();
  throw new Error(
    'Your CRM username is not linked. Sign out and sign in again, or ask an admin to add you under Settings.',
  );
}

export function authorIdForPost(profile: CrmUser | null, users: CrmUser[]): string {
  return projectUpdateAuthorId(profile, users);
}

/** Status-change system log — crm_allowed_emails username "System". */
export function systemAuthorIdForLog(
  profile: CrmUser | null,
  users: CrmUser[],
  _updates: { author_id?: string | null }[],
): string {
  if (users.some((u) => u.username === 'System')) return 'System';
  if (profile?.username?.trim()) return profile.username.trim();
  return 'System';
}
